import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Dropdown, Modal, Card, Button } from "react-bootstrap";
import { format } from "date-fns";
import api from "../api/api";
import {
  Pencil,
  Trash2,
  Copy,
  FileText,
  AlignLeft,
  MoreVertical,
  Eye,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const ProgramActions = ({ program, clientId, onProgramDelete, onPreviewClient, showPreviewInMenu = true }) => {
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const navigate = useNavigate();
  const [summaryProgram, setSummaryProgram] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const summaryRequest = useRef(0);
  const closeSummary = () => {
    summaryRequest.current += 1;
    setShowSummaryModal(false);
  };
  const [copySuccess, setCopySuccess] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const clientUserId = clientId ?? program.client?.user?.id;


  const handleEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!clientUserId) return;
    navigate(`/program/edit/${clientUserId}/${program.id}`);
  };
  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/programs/${program.id}/`);
      if (onProgramDelete) {
        onProgramDelete(program.id);
      }
      if (clientUserId) navigate(`/client/${clientUserId}`);
      setShowDeleteModal(false);
    } catch (error) {
      setShowDeleteModal(false);
      throw new Error(`Error deleting program: ${error.message}`);
    }
  };

  const generateSummaryText = () => {
    if (!summaryProgram) return "";

    let summary = `Plasticise HEP\n`;
    summary += `Title: ${summaryProgram.name}\n`;

    if (summaryProgram.exercises && summaryProgram.exercises.length > 0) {
      summaryProgram.exercises.forEach((exercise, index) => {
        summary += `\n${index + 1}. ${exercise.name}\n`;
        summary += `${exercise.instructions || ""}\n`;
      });
    } else {
      summary += "No activities added to this program.\n";
    }

    return summary;
  };
  const handlePreview = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onPreviewClient) onPreviewClient();
    else {
      const today = format(new Date(), "yyyy-MM-dd");
      navigate(`/program/${program.id}?date=${today}`, { state: { previewClient: true } });
    }
  };

  const handleCopy = () => {
    const text = generateSummaryText();
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleGeneratePDF = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const response = await api.get(`/programs/${program.id}/pdf/`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${program.name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const handleReplicate = () => {
    if (!clientUserId) return;
    navigate(`/program/duplicate/${clientUserId}/${program.id}`);
  };

  const loadSummary = async () => {
    const request = ++summaryRequest.current;
    setSummaryError(null);
    setSummaryProgram(null);
    setSummaryLoading(true);
    try {
      // Client tiles carry a lightweight record without activity instructions.
      const fullProgram = Array.isArray(program.exercises)
        ? program
        : (await api.get(`/programs/${program.id}/`)).data;
      if (!Array.isArray(fullProgram.exercises)) throw new Error("Missing activities");
      if (request === summaryRequest.current) setSummaryProgram(fullProgram);
    } catch (error) {
      if (request === summaryRequest.current) setSummaryError("We couldn't load the program summary. Please try again.");
    } finally {
      if (request === summaryRequest.current) setSummaryLoading(false);
    }
  };

  const handleViewSummary = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSummaryModal(true);
    loadSummary();
    api.post(`/programs/${program.id}/summary-viewed/`).catch((error) => {
      console.error("Unable to track View Summary usage:", error);
    });
  };

  return (
    <>
      <Dropdown align="end">
        <Dropdown.Toggle
          variant="link"
          className="p-0 text-primary border-0 shadow-none"
          id={`program-actions-${program.id}`}
        >
          <MoreVertical size={18} className="text-primary" />
        </Dropdown.Toggle>

        <Dropdown.Menu className="shadow-sm border-0">
          <Dropdown.Item as="button" onClick={handleEdit} disabled={!clientUserId}>
            <Pencil size={16} className="me-2" />Edit
          </Dropdown.Item>
          <Dropdown.Item as="button" onClick={handleViewSummary}>
            <AlignLeft size={16} className="me-2" />View Summary
          </Dropdown.Item>
          <Dropdown.Item as="button" onClick={handleGeneratePDF}>
            <FileText size={16} className="me-2" />Generate PDF
          </Dropdown.Item>
          <Dropdown.Item as="button" onClick={handleReplicate} disabled={!clientUserId}>
            <Copy size={16} className="me-2" />Replicate
          </Dropdown.Item>
          {showPreviewInMenu && <Dropdown.Item as="button" onClick={handlePreview}>
            <Eye size={16} className="me-2" />Preview client view
          </Dropdown.Item>}
          <Dropdown.Divider />
          <Dropdown.Item as="button" onClick={handleDeleteClick} className="text-danger">
            <Trash2 size={16} className="me-2" />Delete
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>

      <Modal show={showSummaryModal} onHide={closeSummary}>
        <Modal.Header closeButton className="border-bottom-0 bg-light">
          <Modal.Title>
            <div className="d-flex align-items-center gap-2">
              <FileText size={20} />
              Program Summary
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          {summaryLoading && <p role="status">Loading program summary...</p>}
          {summaryError && <div role="alert">{summaryError} <Button onClick={loadSummary}>Try again</Button></div>}
          <div className="position-relative">
            <Card className="shadow-sm border-0">
              <Card.Body style={{ backgroundColor: "#f8f9fa" }}>
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "Monaco, monospace",
                    fontSize: "0.9rem",
                    lineHeight: "1.5",
                    backgroundColor: "transparent",
                    margin: 0,
                    padding: "1rem",
                    color: "#333",
                    border: "none",
                  }}
                >
                  {generateSummaryText()}
                </pre>
              </Card.Body>
            </Card>
            <div className="position-sticky bottom-0 pb-3 pt-3 bg-white">
              <Button
                variant={copySuccess ? "success" : "primary"}
                onClick={handleCopy}
                disabled={summaryLoading || !!summaryError || !summaryProgram}
                className="d-flex align-items-center gap-2"
                style={{
                  borderRadius: "20px",
                  padding: "8px 20px",
                  transition: "all 0.2s ease",
                }}
              >
                <Copy size={16} />
                {copySuccess ? "Copied!" : "Copy to Clipboard"}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Program</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete {program?.name}? This action will also
          delete the activities and feedback in the program, and cannot be
          undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

ProgramActions.propTypes = {
  program: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    start_date: PropTypes.string.isRequired,
    end_date: PropTypes.string.isRequired,
    client_name: PropTypes.string.isRequired,
    therapist_name: PropTypes.string.isRequired,
    days_of_week: PropTypes.arrayOf(PropTypes.string).isRequired,
    client: PropTypes.shape({
      id: PropTypes.number.isRequired,
      user: PropTypes.shape({
        id: PropTypes.number.isRequired,
      }).isRequired,
    }),
    exercises: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
      })
    ),
  }).isRequired,
  onPreviewClient: PropTypes.func,
  showPreviewInMenu: PropTypes.bool,
  clientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onProgramDelete: PropTypes.func, // <-- Add this line
};

export default ProgramActions;
